import * as React from "react"
import { router } from "@inertiajs/react"
import type { Entry } from "./types"

/**
 * The section up/down arrows (PageSectionOrder). The server sends entries
 * already in display order; the arrows swap neighbouring movable entries
 * locally, then send the whole movable list — the same idiom as the ranked
 * photo slots. Pinned entries (the hero) keep their slot. The local order is
 * re-derived whenever the server's lands, so a save elsewhere on the screen
 * can never leave it stale.
 */
export function useSectionOrder(pageKey: string | null, entries: Entry[], reorderable: boolean) {
  const movableIds = React.useMemo(
    () => entries.filter((e) => e.movable).map((e) => e.id),
    [entries],
  )
  const [order, setOrder] = React.useState<string[]>(movableIds)
  React.useEffect(() => setOrder(movableIds), [movableIds])

  const orderedEntries = React.useMemo(() => {
    const byId = new Map(entries.map((e) => [e.id, e]))
    const queue = order.map((id) => byId.get(id)).filter((e): e is Entry => e !== undefined)
    return entries.map((e) => (e.movable ? queue.shift() ?? e : e))
  }, [entries, order])

  function moveSection(id: string, delta: number) {
    const index = order.indexOf(id)
    const target = index + delta
    if (!pageKey || index === -1 || target < 0 || target >= order.length) return
    const next = [...order]
    ;[next[index], next[target]] = [next[target], next[index]]
    setOrder(next)
    router.patch(
      `/admin/website/pages/${pageKey}/section-order`,
      { entry_ids: next },
      { preserveScroll: true, preserveState: true },
    )
  }

  function resetOrder() {
    if (pageKey) router.delete(`/admin/website/pages/${pageKey}/section-order`, { preserveScroll: true })
  }

  function moveHandlers(entry: Entry) {
    if (!reorderable || !entry.movable) return undefined
    const index = order.indexOf(entry.id)
    return {
      up: index > 0 ? () => moveSection(entry.id, -1) : undefined,
      down: index < order.length - 1 ? () => moveSection(entry.id, 1) : undefined,
    }
  }

  return { orderedEntries, moveHandlers, resetOrder }
}

/** The one-line explanation shown above a reorderable page's sections. */
export function SectionOrderHint({ customOrder, onReset }: { customOrder: boolean; onReset: () => void }) {
  return (
    <p className="mt-4 mb-6 text-sm text-ink-muted">
      Use the arrows beside each section to change the order it appears in on the page.
      {customOrder && (
        <>
          {" "}
          <button type="button" onClick={onReset} className="text-accent-display underline">
            Reset to the default order
          </button>
        </>
      )}
    </p>
  )
}
